/*
 * Copyright (c) 2021§. World Wide Generation Ltd
 */

import React, { useState, useMemo } from 'react';
import { Modal, ModalBody, ModalHeader, Button } from 'reactstrap';
import { getFileData } from '../../utils/image';
import { FileDropZone } from '../files/FileDropZone';
import { FileListContainer } from '../files/FileListContainer';
import {
  AssurancePortfolioComplete,
  AssuranceQuestionList,
  AssuranceStatus,
  OrganizationAssurancePortfolio,
  UtrvAssurancePopulated,
} from '../../types/assurance';
import { EvidenceFile } from '../survey/question/questionInterfaces';
import { createScopeQuestionGroup } from '../../selectors/survey';
import { BasicAlert } from '../alert/BasicAlert';
import G17Client, { formDataConfig } from '../../services/G17Client';
import { UtrvStatus } from '../../constants/status';
import { DATE, formatDate } from '../../utils/date';
import './complete-assurance-modal.scss';
import { UniversalTrackerValuePlain } from '../../types/surveyScope';
import { QUESTION } from '../../constants/terminology';

interface AssuranceModalProps {
  toggle: (completed?: boolean) => void;
  isOpen: boolean;
  allowConfirm: boolean;
  assurancePortfolio: OrganizationAssurancePortfolio;
}

type CheckboxState = { [key: string]: boolean };
export const CompleteAssuranceModal = ({ toggle, isOpen, assurancePortfolio, allowConfirm }: AssuranceModalProps) => {
  const [assuranceStatement, setAssuranceStatement] = useState<File[]>([]);
  const [checkboxes, setCheckboxState] = useState<CheckboxState>({});

  const questionList: AssuranceQuestionList[] = useMemo(() => {
    const questions = getQuestionList(assurancePortfolio);
    return questions.filter((q) => {
      // Only verified questions can be completed
      if (q.utrv.status !== UtrvStatus.Verified) {
        return false;
      }
      // Filter out assured question for individual assurance
      return allowConfirm ? true : q.status !== AssuranceStatus.Completed;
    });
  }, [assurancePortfolio, allowConfirm]);

  const [completeRequest, setCompleteRequest] = useState({
    submitting: false,
    errored: false,
    errorMessage: ''
  });

  React.useEffect(() => {
    setCheckboxState(questionList.reduce((a, question) => {
      if (question.status === AssuranceStatus.Completed) {
        a[question.assuranceId] = true;
      }
      return a;
    }, {} as CheckboxState));
  }, [questionList]);

  if (!isOpen) {
    return <></>;
  }

  const handleFileAdd = (files: File[]) =>
    files && getFileData(files[0]).then((newFile) => setAssuranceStatement([newFile]));
  const handleFileRemove = () => setAssuranceStatement([]);

  const handleSubmit = () => {
    setCompleteRequest(m => ({ ...m, submitting: true }));
    const ids = Object.entries(checkboxes)
      .filter(([, isSelected]) => isSelected)
      .map(([id]) => id);

    const updateEndpoint = allowConfirm
      ? // FormData is only supported for endpoint with file upload
      G17Client.completeAssurancePortfolio(
        assurancePortfolio._id,
        { files: assuranceStatement, questions: ids } as AssurancePortfolioComplete,
        formDataConfig
      )
      : // Assurance require json
      G17Client.assureQuestions(assurancePortfolio._id, { questions: ids });

    updateEndpoint.then(() => {
      setCompleteRequest(m => ({ ...m, submitting: false }));
      return toggle(true);
    }).catch((e: Error) => setCompleteRequest(m => ({
      ...m,
      submitting: false,
      errored: true,
      errorMessage: e.message
    })));
  };

  const convertToFile = (f: File): EvidenceFile => ({
    type: 'file',
    file: f,
    title: '',
    description: ''
  })

  const isQuestionsSelected = Object.keys(checkboxes).some(c => checkboxes[c]);
  const isAssuranceStatementRequired = allowConfirm;
  const hasAssurance = assuranceStatement.length > 0;
  const canComplete = (!isAssuranceStatementRequired || hasAssurance) && isQuestionsSelected;
  const submitDisabled = !canComplete || completeRequest.submitting;

  const fileZoneBody = <span className={'filezone-text'}>
    <i className='fa fa-file mr-3' />Attach a file by dragging and dropping or <u>selecting</u>
  </span>;

  const title = allowConfirm ? 'Complete Assurance Tracker' : `Mark ${QUESTION.PLURAL} as assured`;

  const getSurveyName = () => {
    if (assurancePortfolio?.survey?.name) {
      return assurancePortfolio?.survey?.name;
    }
    const name = assurancePortfolio?.initiative?.name;
    const date = formatDate(assurancePortfolio.survey?.effectiveDate, DATE.YEAR_MONTH, true);
    return `${name} ${date}`;
  }

  const questionCount = assurancePortfolio.universalTrackerValueAssurances.length;

  return (
    <Modal isOpen={isOpen} toggle={() => toggle(false)} backdrop='static' size='lg' className='assurance-stakeholder-modal'>
      <ModalHeader toggle={() => toggle(false)}>{title}</ModalHeader>
      <ModalBody>
        <BasicAlert hide={!completeRequest.errored} type={'danger'}>{completeRequest.errorMessage}</BasicAlert>
        <p>
          {`This will complete and close the assurance check for ${getSurveyName()}. You have assured ${Object.keys(checkboxes).length
            } of the ${questionCount}
          requested ${QUESTION.PLURAL}. Click ‘Confirm’ to complete your Assurance.`}
        </p>

        {isAssuranceStatementRequired ? (
          <>
            <label className='strong mt-3 ml-2'>Attached Assurance Statement</label>
            <FileDropZone
              disabled={!isQuestionsSelected}
              className={`assurance-dropzone ${!isQuestionsSelected ? 'muted' : ''}`}
              multiple={false}
              onDrop={handleFileAdd}>
              {fileZoneBody}</FileDropZone>
            <FileListContainer
              allowToggle={true}
              files={assuranceStatement.map(convertToFile)}
              handleFileRemoved={handleFileRemove}
            />
          </>
        ) : <></>
        }
        <div className='d-flex mt-3 justify-content-end'>
          <Button color='link-secondary' className='mr-3' onClick={() => toggle(false)} >Cancel</Button>
          <Button onClick={handleSubmit} disabled={submitDisabled} >Confirm</Button>
        </div>
      </ModalBody>
    </Modal>
  );
};

const getQuestionList = (assurancePortfolio: OrganizationAssurancePortfolio): AssuranceQuestionList[] => {
  if (!assurancePortfolio.universalTrackerValueAssurances) {
    return [];
  }

  const surveyData = assurancePortfolio.surveyData;
  const questionGroups = createScopeQuestionGroup(surveyData);

  const findUTrvById = (utrvId: string): undefined | Pick<UniversalTrackerValuePlain, '_id' | 'universalTrackerId' | 'status'> => {
    return surveyData.fragmentUniversalTrackerValues.find(utrv => utrv._id === utrvId);
  }

  const utrMap = new Map<string, UtrvAssurancePopulated[]>();
  assurancePortfolio.universalTrackerValueAssurances.forEach(assurance => {
    const utrv = findUTrvById(assurance.utrvId);
    if (!utrv) {
      return;
    }
    assurance.utrv = utrv;
    const assuranceList = utrMap.get(utrv.universalTrackerId) || [];
    assuranceList.push(assurance as UtrvAssurancePopulated);
    utrMap.set(utrv.universalTrackerId, assuranceList);
  });

  const questionList: AssuranceQuestionList[] = [];
  questionGroups.forEach(({ list }) => {
    list.forEach(({ universalTracker }) => {
      const utr = universalTracker.getRaw();
      if (!utrMap.has(utr._id)) {
        return;
      }

      const assuranceList = utrMap.get(utr._id);
      if (!assuranceList || assuranceList.length === 0) {
        return;
      }

      assuranceList.forEach(assurance => {
        const composite: AssuranceQuestionList = {
          utr,
          utrv: assurance.utrv,
          status: assurance.status,
          assuranceId: assurance._id,
          name: `${utr.type.toUpperCase()} ${utr.typeCode}: ${utr.name}`,
          valueType: assurance.utrv.valueType ?? utr.valueType,
        }
        questionList.push(composite);
      });
    });
  });

  return questionList;
}
